import type { CustomerOrder } from '@noconflict/contracts';
import { CopyIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useOrgContext } from '../../entities/org';
import { useFormat } from '../../shared/i18n/format';
import { Button } from '../../shared/ui/button';
import { toast } from '../../shared/ui/sonner';

/** Where the pay page lives; the link is shareable with anyone who holds `orders.pay`. */
export const payPath = (orgSlug: string, orderId: string) => `/o/${orgSlug}/orders/${orderId}/pay`;

/**
 * An order waiting for a card payment: the amount (when the organization may see it), the date it must be paid by,
 * **Pay now** for someone with `orders.pay`, and the payment link to copy for someone else. `children` adds
 * further actions beside them.
 */
export function OrderPayControls({
  order,
  workspaceId,
  body,
  children,
}: {
  order: CustomerOrder;
  workspaceId: string;
  /** The sentence under the heading; defaults to the plain "pay by card" one. */
  body?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation('order');
  const { org, canIn } = useOrgContext();
  const { formatCurrency, formatDate } = useFormat();
  const canPay = canIn(workspaceId, 'orders.pay');
  const amount =
    order.amount_cents !== null && order.currency
      ? formatCurrency(order.amount_cents, order.currency)
      : null;
  return (
    <div className="grid gap-3">
      <p className="font-bold text-heading">{t('panel.awaitingTitle')}</p>
      <p>{body ?? t('panel.awaitingBody')}</p>
      <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
        {amount ? (
          <div>
            <dt className="text-sm text-muted-foreground">{t('panel.price')}</dt>
            <dd className="font-bold text-heading">{amount}</dd>
          </div>
        ) : null}
        {order.expires_at ? (
          <div>
            <dt className="text-sm text-muted-foreground">{t('panel.payBy')}</dt>
            <dd>{formatDate(order.expires_at)}</dd>
          </div>
        ) : null}
      </dl>
      <div className="flex flex-wrap gap-2">
        {canPay ? (
          <Button asChild variant="accent">
            <Link to={payPath(org.slug, order.id)}>{t('panel.payNow')}</Link>
          </Button>
        ) : null}
        <Button
          variant="secondary"
          onClick={async () => {
            await navigator.clipboard.writeText(
              `${window.location.origin}${payPath(org.slug, order.id)}`,
            );
            toast.success(t('panel.copied'));
          }}
        >
          <CopyIcon aria-hidden="true" />
          {t('panel.copyLink')}
        </Button>
        {children}
      </div>
    </div>
  );
}
